// src/Components/Common/SeqTypesAndGroups/index.js
import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { Card, CardBody, CardHeader, Button, Input, Label } from "reactstrap";
import PolysomeIcon from "../../assets/images/eirna/SeqIcons/POLYSOME SEQ GREEN.png";
import DisomeIcon from "../../assets/images/eirna/SeqIcons/DISOME SEQ GREEN.png";
import MonosomeIcon from "../../assets/images/eirna/SeqIcons/MONOSOME SEQ GREEN.png";
import RNASeqIcon from "../../assets/images/eirna/SeqIcons/RNA SEQ GREEN.png";
import tRNAIcon from "../../assets/images/eirna/SeqIcons/tRNA SEQ GREEN.png";
import { AiOutlineInfoCircle } from "react-icons/ai";

// Styled component for responsive button group
const ResponsiveButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: stretch;

  button {
    flex: 0 0 auto !important;
    min-width: 70px;
    max-width: 150px;
    transition: all 0.2s ease;
  }

  /* For mobile responsiveness */
  @media (max-width: 768px) {
    gap: 2px;

    button {
      font-size: 12px !important;
      padding: 4px 8px !important;
      min-width: 60px;
    }
  }

  /* Handle many items with scrolling option */
  &.scrollable {
    max-height: 180px;
    overflow-y: auto;
    overflow-x: hidden;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 2px;
    }
  }
`;

const GROUP_GRID_COLUMN_COUNT_STORAGE_KEY = "seqTypesAndGroups.groupGridColumns";
const GROUP_SORT_ORDER_STORAGE_KEY = "seqTypesAndGroups.groupSortOrder";

const DEFAULT_GROUP_GRID_COLUMNS = 6;
const MIN_GROUP_GRID_COLUMNS = 1;
const MAX_GROUP_GRID_COLUMNS = 8;

const GROUP_SORT_OPTIONS = [
  { value: "smart", label: "Smart" },
  { value: "natural", label: "Natural" },
  { value: "alphabetical", label: "Alphabetical" },
];

const naturalCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

const CONTROL_TOKENS = new Set([
  "control",
  "ctrl",
  "mock",
  "negative",
  "untreated",
  "vehicle",
  "wildtype",
  "wt",
]);

const clampNumber = (value, min, max) => Math.min(Math.max(value, min), max);

const getStorageValue = (key, fallback) => {
  if (typeof window === "undefined") {
    return fallback;
  }

  try {
    return window.localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};

const setStorageValue = (key, value) => {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Ignore storage failures so the UI still works in restricted browsers.
    }
  }
};

const getStoredGroupGridColumns = () => {
  const storedValue = Number.parseInt(
    getStorageValue(
      GROUP_GRID_COLUMN_COUNT_STORAGE_KEY,
      String(DEFAULT_GROUP_GRID_COLUMNS)
    ),
    10
  );

  if (Number.isNaN(storedValue)) {
    return DEFAULT_GROUP_GRID_COLUMNS;
  }

  return clampNumber(
    storedValue,
    MIN_GROUP_GRID_COLUMNS,
    MAX_GROUP_GRID_COLUMNS
  );
};

const getStoredGroupSortOrder = () => {
  const storedValue = getStorageValue(GROUP_SORT_ORDER_STORAGE_KEY, "smart");
  const allowedValues = GROUP_SORT_OPTIONS.map((option) => option.value);

  return allowedValues.includes(storedValue) ? storedValue : "smart";
};

const getGroupLabel = (group) => String(group?.label ?? group?.value ?? "");

const tokenizeGroupLabel = (label) =>
  label
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const buildSmartSortKey = (group) => {
  const label = getGroupLabel(group);
  const tokens = tokenizeGroupLabel(label);
  const lastToken = tokens[tokens.length - 1] ?? label.toLowerCase();
  const firstToken = tokens[0] ?? label.toLowerCase();
  const controlPriority = tokens.some((token) => CONTROL_TOKENS.has(token))
    ? 0
    : 1;
  const sampleToken =
    tokens.find(
      (token) =>
        /[a-z]+\d+/i.test(token) &&
        !/^lm\d+/i.test(token) &&
        !CONTROL_TOKENS.has(token)
    ) ?? "";
  const doseToken = tokens.find((token) => /^lm\d+/i.test(token)) ?? firstToken;

  return {
    lastToken,
    controlPriority,
    sampleToken,
    doseToken,
    label,
  };
};

const compareText = (a, b) => naturalCollator.compare(a ?? "", b ?? "");

const compareSmartGroups = (a, b, smartClusterOrder = new Map()) => {
  const aKey = buildSmartSortKey(a.group);
  const bKey = buildSmartSortKey(b.group);
  const aClusterOrder =
    smartClusterOrder.get(aKey.lastToken) ?? Number.MAX_SAFE_INTEGER;
  const bClusterOrder =
    smartClusterOrder.get(bKey.lastToken) ?? Number.MAX_SAFE_INTEGER;

  return (
    aClusterOrder - bClusterOrder ||
    compareText(aKey.lastToken, bKey.lastToken) ||
    aKey.controlPriority - bKey.controlPriority ||
    compareText(aKey.sampleToken, bKey.sampleToken) ||
    compareText(aKey.doseToken, bKey.doseToken) ||
    compareText(aKey.label, bKey.label) ||
    a.index - b.index
  );
};

const sortGroups = (groups, sortOrder) => {
  const indexedGroups = groups.map((group, index) => ({ group, index }));

  switch (sortOrder) {
    case "alphabetical":
      return indexedGroups
        .sort(
          (a, b) =>
            getGroupLabel(a.group)
              .toLowerCase()
              .localeCompare(getGroupLabel(b.group).toLowerCase()) ||
            a.index - b.index
        )
        .map(({ group }) => group);
    case "natural":
      return indexedGroups
        .sort(
          (a, b) =>
            compareText(getGroupLabel(a.group), getGroupLabel(b.group)) ||
            a.index - b.index
        )
        .map(({ group }) => group);
    case "smart":
    default: {
      const smartClusterOrder = new Map();

      indexedGroups.forEach(({ group }) => {
        const { lastToken } = buildSmartSortKey(group);

        if (!smartClusterOrder.has(lastToken)) {
          smartClusterOrder.set(lastToken, smartClusterOrder.size);
        }
      });

      return indexedGroups
        .sort((a, b) => compareSmartGroups(a, b, smartClusterOrder))
        .map(({ group }) => group);
    }
  }
};

const GroupSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }
`;

const GroupControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

const GroupControl = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  label {
    color: #6b7280;
    font-size: 11px;
    font-weight: 500;
  }

  .form-control,
  .form-select {
    border-color: #d1d5db;
    color: #6b7280;
    font-size: 11px;
    height: 26px;
    min-height: 26px;
    padding-bottom: 2px;
    padding-top: 2px;
  }

  .group-column-count-input {
    width: 56px;
  }

  .group-sort-select {
    width: 116px;
  }
`;

const GroupGrid = styled.div`
  display: grid;
  gap: 6px;
  grid-template-columns: repeat(
    ${({ $columnCount }) => $columnCount},
    minmax(0, 1fr)
  );
  width: 100%;

  button {
    height: 34px;
    max-width: none !important;
    min-width: 0 !important;
    transition: all 0.2s ease;
    width: 100%;
  }

  @media (max-width: 768px) {
    gap: 4px;

    button {
      font-size: 12px !important;
      padding: 4px 8px !important;
    }
  }
`;

// Styled checkbox container for better responsive handling
const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  .form-check {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .form-check-label {
    font-size: 13px;
    font-weight: 500;
    margin-left: 4px;
    line-height: 1.2;

    small {
      display: inline-block;
      margin-left: 4px;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;

    .form-check-label {
      font-size: 12px;
    }
  }
`;

// Styled section title
const SectionTitle = styled.h6`
  margin-bottom: 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;


// Tooltip icon
const InfoTooltip = ({ id, content, placement = "top" }) => (
  <>
    <AiOutlineInfoCircle
      data-tooltip-id={id}
      title={content}
      style={{
        marginLeft: "4px",
        cursor: "help",
        color: "#6B7280",
        fontSize: "16px",
        verticalAlign: "middle",
        display: "inline-flex",
        alignItems: "center",
      }}
    />
  </>
);

const SeqTypesAndGroups = ({
  // Data props
  seqTypes = [],
  groups = [],
  selectedSeqType,
  selectedGroup,

  // Event handlers
  onSelectSeqType,
  onSelectGroup,

  // File selection props (optional)
  onSelectAllSeqTypeFiles,

  // Configuration props
  showSelectAllCheckboxes = true, // Control whether to show checkboxes
  showSeqTypes = true, // Control whether to show seq types section
  showGroups = true, // Control whether to show groups section
  customTitle = "Seq Types & Groups", // Custom title
  customIcon = "ri-tools-fill", // Custom icon
  maxVisibleSeqTypes = 6, // When to make seq types scrollable

  // Custom styling
  cardStyle = {},
  buttonStyle = {},

  // Custom labels
  seqTypeCheckboxLabel = "All Files in Selected Seq Type",
  groupCheckboxLabel = "All files in group",
}) => {
  const [groupGridColumns, setGroupGridColumns] = useState(
    getStoredGroupGridColumns
  );
  const [groupSortOrder, setGroupSortOrder] = useState(
    getStoredGroupSortOrder
  );

  const sortedGroups = useMemo(
    () => sortGroups(groups, groupSortOrder),
    [groups, groupSortOrder]
  );

  const effectiveGroupGridColumnCount = Math.min(
    groupGridColumns,
    Math.max(sortedGroups.length, 1)
  );

  const handleGroupGridColumnsChange = (event) => {
    const nextColumnCount = Number.parseInt(event.target.value, 10);
    const nextValue = Number.isNaN(nextColumnCount)
      ? DEFAULT_GROUP_GRID_COLUMNS
      : clampNumber(nextColumnCount, MIN_GROUP_GRID_COLUMNS, MAX_GROUP_GRID_COLUMNS);

    setGroupGridColumns(nextValue);
    setStorageValue(GROUP_GRID_COLUMN_COUNT_STORAGE_KEY, String(nextValue));
  };

  const handleGroupSortOrderChange = (event) => {
    const nextValue = event.target.value;
    setGroupSortOrder(nextValue);
    setStorageValue(GROUP_SORT_ORDER_STORAGE_KEY, nextValue);
  };

  const getSeqTypeIcon = (type) => {
    switch (type.toLowerCase()) {
      case "riboseq": // or 'polysomeseq' as needed
        return (
          <img
            src={MonosomeIcon}
            alt="Ribo Seq"
            style={{ width: 25, height: 25, marginRight: 6 }}
          />
        );
      case "rnaseq":
        return (
          <img
            src={RNASeqIcon}
            alt="RNA-Seq icon"
            style={{ width: 22, height: 22, marginRight: 6 }}
          />
        );
      case "disomeseq":
        return (
          <img
            src={DisomeIcon}
            alt="Disome icon"
            style={{ width: 22, height: 22, marginRight: 6 }}
          />
        );
      case "trnaseq":
        return (
          <img
            src={tRNAIcon}
            alt="tRNA-Seq icon"
            style={{ width: 22, height: 22, marginRight: 6 }}
          />
        );
      case "polysomeseq":
        return (
          <img
            src={PolysomeIcon}
            alt="Polysome icon"
            style={{ width: 22, height: 22, marginRight: 6 }}
          />
        );
      default:
        return null;
    }
  };

  const getSeqTypeLabel = (type) => {
    switch (type) {
      case "riboseq":
        return "Ribo-Seq";
      case "rnaseq":
        return "RNA-Seq";
      case "disomeseq":
        return "Disome-Seq";
      case "polysomeseq":
        return "Polysome-Seq";
      default:
        return type;
    }
  };

  // When called will check all files with currently selected seq-type
  const checkAllSeqtypeCheckboxes = useCallback(() => {
    onSelectAllSeqTypeFiles(true);
  }, [onSelectAllSeqTypeFiles]);

  // When called will uncheck all files with currently selected seq-type
  const uncheckAllSeqtypeCheckboxes = useCallback(() => {
    onSelectAllSeqTypeFiles(false);
  }, [onSelectAllSeqTypeFiles]);

  const getButtonStyles = (isSelected) => ({
    backgroundColor: isSelected ? "#33a849" : "#f8f9fa",
    borderColor: "#d1d2d3ff",
    color: isSelected ? "white" : "#6b7280",
    fontWeight: "500",
    fontSize: "13px",
    padding: "6px 12px",
    borderRadius: "6px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    ...buttonStyle, // Allow custom button styling
  });

  // Helper to check if checkboxes should be disabled
  const isSeqTypeCheckboxDisabled =
    !selectedSeqType || selectedSeqType === "all";
  const isGroupCheckboxDisabled = !selectedGroup || selectedGroup === "all";

  return (
    <Card
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "1px",
        height: "100%",
        minHeight: "200px",
        ...cardStyle,
      }}
    >
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center">
          <h5
            className="mb-0 fw-semibold d-flex align-items-center"
            style={{ color: "#1a6d51" }}
          >
            <i className={`${customIcon} me-2`}></i> {customTitle}
          </h5>
        </div>
      </CardHeader>
      <CardBody style={{ padding: "20px" }}>

        {/*Only show check/uncheck buttons if this is a checkbox style
         input and project has >1 group*/}
        {showSelectAllCheckboxes && onSelectAllSeqTypeFiles && groups.length > 1 && (
          <div className="d-flex align-items-center justify-content-end gap-2">
            {/* Uncheck All Button */}
            <Button
              id="uncheckAllSeqtypeBtn"
              size="sm"
              color="outline-danger"
              onClick={ uncheckAllSeqtypeCheckboxes }
            >
            Uncheck All {selectedSeqType} files
            </Button>

            {/* Check All Button */}
            <Button
              id="checkAllSeqtypeBtn"
              size="sm"
              color="outline-primary"
              onClick={ checkAllSeqtypeCheckboxes }
            >
            Check All {selectedSeqType} files
            </Button>
            <InfoTooltip
              id="seq-type-shortcut-btn-tt"
              content={`Uncheck or Check all ${selectedSeqType} files, regardless of which group they belong to`}
            />

          </div>
        )}
        
        {/* Seq Types Section */}
        {showSeqTypes && seqTypes.length > 0 && (
          <div className="mb-4">

            <SectionTitle>Seq-type</SectionTitle>

            {/* Responsive Seq Type Buttons */}
            <ResponsiveButtonGroup
              className={
                seqTypes.length > maxVisibleSeqTypes ? "scrollable" : ""
              }
            >
              {seqTypes.map((type) => (
                <Button
                  key={type.value}
                  id={`seq-type-${type.value}`}
                  onClick={() => onSelectSeqType(type.value)}
                  style={getButtonStyles(selectedSeqType === type.value)}
                >
                  {/* <i
                    className={`${getSeqTypeIcon(type.value)} me-1`}
                    style={{ fontSize: "14px" }}
                  /> */}
                  {getSeqTypeIcon(type.value)} {getSeqTypeLabel(type.value)}
                  {/* <span>{getSeqTypeLabel(type.value)}</span> */}
                </Button>
              ))}
            </ResponsiveButtonGroup>
          </div>
        )}

        {/* Groups Section */}
        {showGroups && groups.length > 0 && (
          <div>
            <GroupSectionHeader>
              <SectionTitle>Groups</SectionTitle>

              <GroupControls>
                <GroupControl>
                  <Label htmlFor="group-grid-column-count" className="mb-0">
                    Columns
                  </Label>
                  <Input
                    id="group-grid-column-count"
                    aria-label="Group grid column count"
                    className="group-column-count-input"
                    min={MIN_GROUP_GRID_COLUMNS}
                    max={MAX_GROUP_GRID_COLUMNS}
                    onChange={handleGroupGridColumnsChange}
                    type="number"
                    value={groupGridColumns}
                  />
                </GroupControl>

                <GroupControl>
                  <Label htmlFor="group-sort-order" className="mb-0">
                    Sort
                  </Label>
                  <Input
                    id="group-sort-order"
                    aria-label="Group sort order"
                    className="group-sort-select"
                    onChange={handleGroupSortOrderChange}
                    type="select"
                    value={groupSortOrder}
                  >
                    {GROUP_SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </GroupControl>
              </GroupControls>
            </GroupSectionHeader>

            {/* Grid Groups Buttons */}
            <GroupGrid
              $columnCount={effectiveGroupGridColumnCount}
              aria-label="Group grid"
            >
              {sortedGroups.map((group) => (
                <Button
                  key={group.value}
                  id={`group-${group.value}`}
                  onClick={() => onSelectGroup(group.value)}
                  style={{
                    ...getButtonStyles(selectedGroup === group.value),
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    justifyContent: "flex-start",
                  }}
                  title={group.label}
                >
                  <i
                    className="ri-group-line me-1"
                    style={{ flex: "0 0 auto", fontSize: "14px" }}
                  />
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {group.label}
                  </span>
                </Button>
              ))}
            </GroupGrid>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default SeqTypesAndGroups;
